import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Radar, Mail, Lock, Loader2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/lib/auth";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // If the user was bounced here from a gated route, we come back to it after login.
  const redirectTo = location.state?.from || "/feed";

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    setError("");
    try {
      await login(email.trim().toLowerCase(), password);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Sign-in failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="login-page"
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#0b1120] px-6"
    >
      <div className="w-full max-w-sm">
        <Link to="/" className="mb-6 flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center border border-[#3b82f6]/40 bg-[#3b82f6]/10">
            <Radar className="h-5 w-5 text-[#3b82f6]" />
          </div>
          <div className="leading-tight">
            <div className="text-base font-bold tracking-tight text-[#f8fafc]">
              SIGNAL EDGE
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#64748b]">
              convergence intel
            </div>
          </div>
        </Link>

        <div className="border border-[#1e293b] bg-[#111827] p-6">
          <div className="mb-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569]">
            Welcome back
          </div>
          <h1 className="text-xl font-bold tracking-tight text-[#f8fafc]">Sign in</h1>
          <p className="mt-1 text-sm text-[#94a3b8]">
            Use the email and password you set at signup.
          </p>

          <form onSubmit={onSubmit} className="mt-5 space-y-3">
            <div className="relative">
              <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#64748b]" />
              <input
                type="email"
                autoFocus
                required
                autoComplete="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                disabled={loading}
                className="w-full border border-[#1e293b] bg-[#0f172a] py-2.5 pl-10 pr-3 text-sm text-[#f8fafc] placeholder:text-[#64748b] focus:border-[#3b82f6] focus:outline-none focus:ring-1 focus:ring-[#3b82f6] disabled:opacity-60"
              />
            </div>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#64748b]" />
              <input
                type="password"
                required
                autoComplete="current-password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="password"
                disabled={loading}
                className="w-full border border-[#1e293b] bg-[#0f172a] py-2.5 pl-10 pr-3 text-sm text-[#f8fafc] placeholder:text-[#64748b] focus:border-[#3b82f6] focus:outline-none focus:ring-1 focus:ring-[#3b82f6] disabled:opacity-60"
              />
            </div>
            {error && (
              <div
                data-testid="login-error"
                role="alert"
                className="border border-[#f43f5e]/30 bg-[#f43f5e]/10 px-3 py-2 text-xs text-[#f43f5e]"
              >
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading || !email || !password}
              data-testid="login-submit-button"
              className="inline-flex w-full items-center justify-center gap-2 border border-[#3b82f6] bg-[#3b82f6] px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-white transition-colors hover:bg-[#2563eb] disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Signing in…
                </>
              ) : (
                "Sign in"
              )}
            </button>
          </form>

          <div className="mt-3 text-right text-[11px]">
            <Link
              to="/forgot-password"
              data-testid="login-forgot-password-link"
              className="text-[#64748b] hover:text-[#94a3b8]"
            >
              Forgot password?
            </Link>
          </div>

          <div className="mt-4 text-center text-[11px] text-[#94a3b8]">
            No account?{" "}
            <Link
              to="/signup"
              data-testid="login-goto-signup"
              className="text-[#3b82f6] hover:text-[#60a5fa]"
            >
              Create one
            </Link>
          </div>
        </div>

        <div className="mt-4 text-center text-[11px] leading-relaxed text-[#64748b]">
          <Link to="/" className="hover:text-[#94a3b8]">← Back to home</Link>
        </div>
      </div>
    </div>
  );
}
